import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLogoLoader from '@/components/common/AppLogoLoader';
import CommonHeader from '@/components/layout/CommonHeader';
import { useUploadContentCustom } from '@/features/content/hooks/useContent';
import { toast } from 'react-toastify';

const TakePhoto: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [photoTaken, setPhotoTaken] = useState(false);
  const [photoData, setPhotoData] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const navigate = useNavigate();
  
  // Use the content upload hook
  const [uploadContent] = useUploadContentCustom();
  
  // Initialize camera when component mounts
  useEffect(() => {
    startCamera();
    
    // Cleanup function to stop camera when component unmounts
    return () => {
      stopCamera();
    };
  }, []);
  
  // Handle video element load
  const handleVideoLoad = () => {
    // Add a slight delay to ensure the video is displaying properly
    setTimeout(() => {
      setShowVideo(true);
    }, 300);
  };
  
  const startCamera = async () => {
    setShowVideo(false);
    setIsLoading(true);
    setPermissionError(false);
    
    // Add a 1-second delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera by default on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = handleVideoLoad;
        setCameraActive(true);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setPermissionError(true);
      setCameraActive(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const video = videoRef.current;
      const stream = video.srcObject as MediaStream;
      const tracks = stream.getTracks();
      
      // Pause the video first
      video.pause();
      
      // Stop all tracks (video and audio if any)
      tracks.forEach(track => {
        track.stop();
        console.log('Stopped track:', track.kind, track.label);
      });
      
      // Clear the video source and reset
      video.srcObject = null;
      video.load(); // Force reload to clear any cached frames
      
      setCameraActive(false);
      setShowVideo(false);
      
      console.log('Camera stopped - indicator should be off');
    }
  };
  
  const takePhoto = () => {
    if (!canvasRef.current || !videoRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Get photo data URL
      const photoDataUrl = canvas.toDataURL('image/jpeg');
      setPhotoData(photoDataUrl);
      setPhotoTaken(true);
      
      // Stop the camera after taking photo to turn off camera usage indicator
      stopCamera();
    }
  };
  
  const retake = () => {
    setPhotoTaken(false);
    setPhotoData(null);
    setShowVideo(false);
    setCameraActive(false);
    setPermissionError(false);
    startCamera();
  };
  
  const saveContent = async () => {
    // Show saving state
    setIsSaving(true);
    
    try {
      if (!photoData) {
        throw new Error("No photo to upload");
      }
      
      // Convert base64 image to file
      const response = await fetch(photoData);
      const blob = await response.blob();
      const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
      
      // Call the upload content hook from our feature
      const result = await uploadContent(file, (progress: number) => {
        // Progress tracking - could be used for UI feedback if needed
        Math.min(progress, 99);
      });
      
      if (result.data?.uploadContent?.success) {
        toast.success(result.data.uploadContent.message || 'Photo uploaded successfully!');
        
        // Navigate to discover after a brief delay
        setTimeout(() => {
          navigate('/discover');
        }, 1000);
      } else {
        const errorMessage = result.data?.uploadContent?.message || 'Upload failed. Please try again.';
        toast.error(errorMessage);
        setIsSaving(false);
      }
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to upload photo. Please try again.');
      setIsSaving(false);
    }
  };

  return (
    <div className="h-dvh bg-black text-white flex flex-col">
      {/* Header */}
      <CommonHeader
        title="Take a photo"
        onBackClick={() => navigate('/discover')}
      />
      
      {/* Camera Preview / Photo Result / Permission Error / Saving */}
      <div className="flex-1 flex items-center justify-center bg-black relative overflow-hidden">
        {isLoading ? (
          <AppLogoLoader size="medium" text="Accessing camera..." />
        ) : isSaving ? (
          <AppLogoLoader size="medium" text="Saving your photo..." />
        ) : permissionError ? (
          <div className="text-center px-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 mx-auto mb-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-white text-xl font-bold mb-4">Camera Access Denied</h2>
            <p className="text-gray-400 mb-8">
              Please allow camera access to take photos. Check your browser settings to enable camera permissions.
            </p>
            <div className="flex justify-center">
              <button 
                onClick={startCamera}
                className="btn btn-primary px-8 py-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <AppLogoLoader size="small" className="py-2" />
                ) : "Try Again"}
              </button>
            </div>
          </div>
        ) : photoTaken && photoData ? (
          <>
            {/* Photo preview with same layout as camera */}
            <img 
              src={photoData} 
              alt="Captured" 
              className="w-full h-full object-cover"
            />
          </>
        ) : (
          <>
            {!showVideo && !isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <AppLogoLoader size="small" text="Preparing camera..." />
              </div>
            )}
            <video 
              ref={videoRef}
              autoPlay 
              playsInline
              className={`w-full h-full object-cover ${showVideo ? 'opacity-100' : 'opacity-0'}`}
              style={{ transition: 'opacity 0.3s ease-in-out' }}
            />
          </>
        )}
      </div>
      
      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Footer with photo button */}
      {!permissionError && !isSaving && (
        <div className="relative">
          {photoTaken ? (
            /* Save/Retake buttons with absolute positioning */
            <div 
              style={{
                position: 'absolute',
                bottom: '25px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '16px'
              }}
            >
              <button 
                onClick={retake}
                className="btn btn-secondary"
                disabled={isSaving}
              >
                Retake
              </button>
              <button 
                onClick={saveContent}
                className="btn btn-primary"
                disabled={isSaving}
              >
                Save
              </button>
            </div>
          ) : (
            /* Photo button with absolute positioning */
            <button 
              onClick={takePhoto}
              disabled={!cameraActive || !showVideo}
              style={{
                position: 'absolute',
                bottom: '25px',
                left: '50%',
                marginLeft: '-40px',
                width: '80px',
                height: '80px',
                border: '4px solid #fff',
                borderRadius: '40px',
                background: 'var(--primary, linear-gradient(94deg, #FF2A2E 0%, #FEB03A 100%))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div className="w-16 h-16 rounded-full bg-white"></div>
            </button>
          )}
        </div>
      )}

      {/* Upload Progress Overlay */}
      {isSaving && (
        <div 
          className="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh'
          }}
        >
          {/* Blurred and darkened background overlay */}
          <div 
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              width: '100%',
              height: '100%'
            }}
          />
          
          {/* Upload progress content */}
          <div 
            className="relative z-10 text-center text-white px-6"
            style={{ zIndex: 10 }}
          >
            {/* Animated upload spinner */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                {/* Outer spinning ring */}
                <svg 
                  className="w-20 h-20 animate-spin" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="url(#uploadSpinnerGradient)"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="60 40"
                    className="animate-spin"
                  />
                  <defs>
                    <linearGradient id="uploadSpinnerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FF2A2E" />
                      <stop offset="100%" stopColor="#FEB03A" />
                    </linearGradient>
                  </defs>
                </svg>
                
                {/* Inner pulsing circle */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div 
                    className="w-8 h-8 rounded-full animate-pulse"
                    style={{
                      background: 'linear-gradient(45deg, #FF2A2E 0%, #FEB03A 100%)',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                </div>
                
                {/* Upload icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg 
                    className="w-6 h-6 text-white animate-bounce" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ animationDuration: '3s' }}
                  >
                    <path 
                      d="M12 2L12 8M12 8L9 5M12 8L15 5" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Upload text and progress */}
            <div className="space-y-3">
              <p className="text-xl font-semibold">Uploading photo...</p>
              <p className="text-sm text-white/80 max-w-xs mx-auto leading-relaxed">
                Please wait while we process your photo
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakePhoto; 